import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import './EditManufacturerDrawer.css'
import type { EditContext, ManufacturerDraft, ManufacturerRow } from './ManufacturerList.types'

export interface EditManufacturerDrawerProps {
  open: boolean
  onClose: () => void
  context: EditContext | null
  manufacturer: ManufacturerRow | null
  onSave: (draft: ManufacturerDraft) => void
  onDelete: () => void
}

export function EditManufacturerDrawer({
  open,
  onClose,
  context,
  manufacturer,
  onSave,
  onDelete,
}: EditManufacturerDrawerProps) {
  const [draft, setDraft] = useState<ManufacturerDraft>({
    provider: '',
    alias: '',
    mfrCodeUpper: '',
    url: '',
    direct: false,
  })

  // Seed draft when opening / switching selected manufacturer
  useEffect(() => {
    if (!open) return

    setDraft({
      provider: manufacturer?.provider ?? '',
      alias: context?.kind === 'alias' ? manufacturer?.aliases[context.aliasIndex] ?? '' : '',
      mfrCodeUpper: manufacturer?.mfrCodeUpper ?? '',
      url: manufacturer?.url ?? '',
      direct: false,
    })
  }, [open, manufacturer, context])

  const canSave = useMemo(() => draft.provider.trim().length > 0, [draft.provider])
  const isAliasEdit = context?.kind === 'alias'

  const copyUrl = async () => {
    if (!draft.url) return
    try {
      await navigator.clipboard.writeText(draft.url)
    } catch {
      // ignore
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: 'clamp(480px, 50vw, 1200px)', display: 'flex', flexDirection: 'column' },
        },
      }}
    >
      {/* Header */}
      <div className="edit-manufacturer-drawer-header">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Edit Manufacturers
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" aria-label="Next">
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </div>

      <Divider />

      <div className="edit-manufacturer-drawer-body">
        <div className="edit-manufacturer-drawer-panel">
          <div className="edit-manufacturer-drawer-fields">
            <TextField
              label="Manufacturer"
              value={draft.provider}
              onChange={e => setDraft((prev: ManufacturerDraft) => ({ ...prev, provider: e.target.value }))}
              fullWidth
              size="small"
            />

            <TextField
              label="Alias"
              value={draft.alias}
              disabled={!isAliasEdit}
              helperText={!isAliasEdit ? 'Expand a manufacturer and click an alias row to edit it.' : undefined}
              onChange={e => setDraft((prev: ManufacturerDraft) => ({ ...prev, alias: e.target.value }))}
              fullWidth
              size="small"
            />

            <TextField
              label="OEM Code"
              value={draft.mfrCodeUpper}
              onChange={e => setDraft((prev: ManufacturerDraft) => ({ ...prev, mfrCodeUpper: e.target.value }))}
              fullWidth
              size="small"
            />

            <TextField
              label="URL"
              value={draft.url}
              onChange={e => setDraft((prev: ManufacturerDraft) => ({ ...prev, url: e.target.value }))}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Copy URL" onClick={copyUrl}>
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.direct}
                  onChange={e => setDraft((prev: ManufacturerDraft) => ({ ...prev, direct: e.target.checked }))}
                  size="small"
                />
              }
              label="Direct"
            />

            <div className="edit-manufacturer-drawer-actions">
              <Button variant="contained" disabled={!canSave} onClick={() => onSave(draft)} sx={{ minWidth: 120 }}>
                SAVE
              </Button>
              {!isAliasEdit && (
                <Button variant="outlined" color="error" onClick={onDelete} sx={{ minWidth: 120 }}>
                  DELETE
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  )
}
